import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { claimLead, fetchLeadSection } from '../api';
import { usePolling } from '../hooks/usePolling';
import type { WhatsAppListActivityEvent } from '../hooks/useWhatsAppListWebSocket';
import type { Lead, LeadQueueSummary, LeadStatus } from '../types';
import LeadCard from './LeadCard';
import { AgentStatusWidget } from './AgentStatusWidget';
import WaitingForYou from './WaitingForYou';
import ArchivedLeads from './ArchivedLeads';

type LeadView = 'unassigned' | 'queue';
type TierFilter = 'all' | '1' | '2' | '3';
type DateFilter = 'all' | 'today' | 'yesterday' | '3d' | '5d' | '7d' | 'custom';
const PAGE_SIZE = 50;

const SOURCE_FILTERS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'meta_form', label: 'Meta form' },
  { value: 'call', label: 'Call' },
  { value: 'neodove', label: 'NeoDove' },
] as const;

const STATUS_FILTERS: Array<{ value: LeadStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'qualified', label: 'Qualified' },
];

const DATE_FILTERS: Array<{ value: DateFilter; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '3d', label: 'Last 3 days' },
  { value: '5d', label: 'Last 5 days' },
  { value: '7d', label: 'Last week' },
  { value: 'custom', label: 'Specific date' },
];

const TIER_FILTERS: Array<{ value: TierFilter; label: string; tone: string }> = [
  { value: 'all', label: 'All', tone: 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100' },
  { value: '1', label: 'Hot', tone: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100' },
  { value: '2', label: 'Warm', tone: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' },
  { value: '3', label: 'Cold', tone: 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100' },
];

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function shiftDays(daysAgo: number, date = new Date()) {
  return startOfLocalDay(new Date(date.getFullYear(), date.getMonth(), date.getDate() - daysAgo)).toISOString();
}

function parseLocalDateString(dateStr: string) {
  if (!dateStr) return null;
  return new Date(`${dateStr}T00:00:00`);
}

function formatShortDate(value: string) {
  const date = parseLocalDateString(value);
  return date?.toLocaleDateString([], { day: 'numeric', month: 'short' }) || '';
}

export function queueCreatedRange(
  selectedDateFilter: DateFilter,
  customFrom: string,
  customTo: string,
  now = new Date()
) {
  const today = startOfLocalDay(now);
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (selectedDateFilter === 'today') {
    return { createdFrom: today.toISOString(), createdTo: tomorrow.toISOString() };
  }
  if (selectedDateFilter === 'yesterday') {
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    return { createdFrom: yesterday.toISOString(), createdTo: today.toISOString() };
  }
  if (selectedDateFilter === '3d') {
    return { createdFrom: shiftDays(3, today), createdTo: tomorrow.toISOString() };
  }
  if (selectedDateFilter === '5d') {
    return { createdFrom: shiftDays(5, today), createdTo: tomorrow.toISOString() };
  }
  if (selectedDateFilter === '7d') {
    return { createdFrom: shiftDays(7, today), createdTo: tomorrow.toISOString() };
  }
  if (selectedDateFilter === 'custom') {
    const fromDate = parseLocalDateString(customFrom);
    const toDate = parseLocalDateString(customTo);
    return {
      createdFrom: fromDate ? startOfLocalDay(fromDate).toISOString() : customFrom || undefined,
      createdTo: toDate
        ? new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate() + 1).toISOString()
        : customTo || undefined,
    };
  }
  return { createdFrom: undefined, createdTo: undefined };
}

export default function QueueTab({
  active,
  onSelect,
  onChanged,
  reloadKey,
  me,
  selectedId,
  view,
  title,
  subtitle,
  desktop = false,
  queueSseConnectionState = 'disconnected',
  queueSseSignalKey,
  whatsappActivityEvent = null,
  canRestoreArchived = false,
}: {
  active: boolean;
  onSelect: (id: string) => void;
  onChanged: () => void;
  reloadKey: number;
  me: string;
  selectedId?: string | null;
  view: LeadView;
  title: string;
  subtitle: string;
  desktop?: boolean;
  queueSseConnectionState?: 'connecting' | 'connected' | 'disconnected';
  queueSseSignalKey?: string | null;
  whatsappActivityEvent?: WhatsAppListActivityEvent | null;
  canRestoreArchived?: boolean;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Array<LeadStatus | 'all'>>(['all']);
  const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilter>('all');
  const [selectedTierFilter, setSelectedTierFilter] = useState<TierFilter>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<LeadQueueSummary | null>(null);
  const [rawRings, setRawRings] = useState<number | null>(null);
  const [newWhatsAppLeadIds, setNewWhatsAppLeadIds] = useState<Set<string>>(() => new Set());
  const [isPageVisible, setIsPageVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  const leadsLengthRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const leadsRef = useRef<Lead[]>([]);
  const loadRef = useRef<((options?: { reset?: boolean }) => Promise<void>) | null>(null);
  const inFlightLoadsRef = useRef(new Map<string, Promise<unknown>>());
  const latestLoadKeyRef = useRef<string | null>(null);
  const reconcileTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const sourcesKey = selectedSources.join(',');
  const selectedSourceList = useMemo(
    () => (sourcesKey ? sourcesKey.split(',').filter(Boolean) : []),
    [sourcesKey]
  );
  const selectedStatusList = useMemo(
    () => (selectedStatuses.includes('all') ? [] : selectedStatuses.filter((status): status is LeadStatus => status !== 'all')),
    [selectedStatuses]
  );
  const activeFilterCount =
    (selectedStatusList.length > 0 ? 1 : 0) +
    (selectedSourceList.length > 0 ? 1 : 0) +
    (selectedDateFilter !== 'all' ? 1 : 0) +
    (selectedTierFilter !== 'all' ? 1 : 0);
  const queueSignalKey = view === 'queue' ? queueSseSignalKey ?? null : null;

  const createdRange = useMemo(
    () => queueCreatedRange(selectedDateFilter, customFrom, customTo),
    [customFrom, customTo, selectedDateFilter]
  );

  const sortedLeads = useMemo(() => {
    const activityTime = (lead: Lead) => new Date(
      (view === 'queue' ? lead.last_activity_at : null) || lead.created_at
    ).getTime();
    return [...leads].sort(
      (a, b) => (sortOrder === 'newest' ? 1 : -1) *
        (activityTime(b) - activityTime(a))
    );
  }, [leads, sortOrder, view]);


  const selectedDateLabel = useMemo(() => {
    if (selectedDateFilter === 'custom') {
      const from = formatShortDate(customFrom);
      const to = formatShortDate(customTo);
      return from && to ? `${from} – ${to}` : from || to || 'Date range';
    }
    return DATE_FILTERS.find((filter) => filter.value === selectedDateFilter)?.label || 'All time';
  }, [customFrom, customTo, selectedDateFilter]);

  useEffect(() => {
    leadsLengthRef.current = leads.length;
    leadsRef.current = leads;
  }, [leads]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    onVisibilityChange();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const visibleLeads = useMemo(() => {
    let filtered = sortedLeads;

    if (view === 'unassigned' && selectedSourceList.length > 0) {
      filtered = filtered.filter((lead) => selectedSourceList.includes(lead.source));
    }

    if (view === 'unassigned' && selectedTierFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.tier_score === Number(selectedTierFilter));
    }

    if (view === 'queue' && selectedStatusList.length > 0) {
      filtered = filtered.filter((lead) => selectedStatusList.includes(lead.status));
    }

    return filtered;
  }, [selectedSourceList, selectedStatusList, selectedTierFilter, sortedLeads, view]);

  const sourceCounts = useMemo(() => {
    if (view === 'unassigned' && summary) {
      const counts = { ...summary.sources };
      if (selectedSourceList.length === 1) {
        counts[selectedSourceList[0]] = totalCount;
      }
      return counts;
    }
    return sortedLeads.reduce<Record<string, number>>((acc, lead) => {
      acc[lead.source] = (acc[lead.source] || 0) + 1;
      return acc;
    }, {});
  }, [selectedSourceList, sortedLeads, summary, totalCount, view]);

  const tierCounts = useMemo(() => {
    if (view === 'unassigned' && summary) {
      return { hot: summary.hot, warm: summary.warm, cold: summary.cold };
    }
    return sortedLeads.reduce(
      (acc, lead) => {
        if (lead.tier_score === 1) acc.hot += 1;
        if (lead.tier_score === 2) acc.warm += 1;
        if (lead.tier_score === 3) acc.cold += 1;
        return acc;
      },
      { hot: 0, warm: 0, cold: 0 }
    );
  }, [sortedLeads, summary, view]);

  const load = useCallback(async (options?: { reset?: boolean }) => {
    const reset = Boolean(options?.reset);
    if (!reset && view === 'unassigned' && (loadingMoreRef.current || !hasMoreRef.current)) {
      return;
    }
    const requestKey = JSON.stringify({
      view,
      reset,
      offset: reset ? 0 : leadsLengthRef.current,
      source: selectedSourceList,
      statuses: selectedStatusList,
      tier: selectedTierFilter,
      createdFrom: createdRange.createdFrom,
      createdTo: createdRange.createdTo,
    });
    latestLoadKeyRef.current = requestKey;
    try {
      if (reset) {
        setRefreshing(true);
        setLoading(true);
        setLeads([]);
        setHasMore(false);
        hasMoreRef.current = false;
        setSummary(null);
        setTotalCount(0);
      } else {
        setLoadingMore(true);
        loadingMoreRef.current = true;
      }

      let request = inFlightLoadsRef.current.get(requestKey);
      if (!request) {
        request = fetchLeadSection(view, {
          limit: PAGE_SIZE,
          offset: reset ? 0 : leadsLengthRef.current,
          source: selectedSourceList.length > 0 ? selectedSourceList : undefined,
          statuses: selectedStatusList.length > 0 ? selectedStatusList : undefined,
          tier: selectedTierFilter !== 'all' ? (Number(selectedTierFilter) as 1 | 2 | 3) : undefined,
          createdFrom: createdRange.createdFrom,
          createdTo: createdRange.createdTo,
        });
        inFlightLoadsRef.current.set(requestKey, request);
        void request.finally(() => {
          if (inFlightLoadsRef.current.get(requestKey) === request) {
            inFlightLoadsRef.current.delete(requestKey);
          }
        }).catch(() => undefined);
      }
      const d = await request as Awaited<ReturnType<typeof fetchLeadSection>>;
      if (latestLoadKeyRef.current !== requestKey) return;
      setLeads((current) => (reset ? d.leads : [...current, ...d.leads]));
      const nextHasMore = Boolean(d.has_more);
      setHasMore(nextHasMore);
      hasMoreRef.current = nextHasMore;
      if (typeof d.total === 'number') {
        setTotalCount(d.total);
      } else if (d.summary) {
        setTotalCount(d.summary.total);
      }
      if (view === 'unassigned' && d.summary) {
        setSummary(d.summary);
      }
      setRawRings(typeof d.raw_rings === 'number' ? d.raw_rings : null);
      setErr(null);
    } catch (e) {
      if (latestLoadKeyRef.current === requestKey) {
        setErr(e instanceof Error ? e.message : 'load failed');
      }
    } finally {
      if (latestLoadKeyRef.current === requestKey) {
        setRefreshing(false);
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    }
  }, [createdRange.createdFrom, createdRange.createdTo, selectedSourceList, selectedStatusList, selectedTierFilter, view]);

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  const scheduleResetLoad = useCallback(() => {
    if (!active || !isPageVisible || refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = undefined;
      void loadRef.current?.({ reset: true });
    }, 100);
  }, [active, isPageVisible]);

  const handleWhatsAppActivity = useCallback((event: WhatsAppListActivityEvent) => {
    const incomingTime = new Date(event.activity_at).getTime();
    const currentLead = leadsRef.current.find((lead) => lead.id === event.lead_id);

    if (!currentLead) {
      if (!reconcileTimerRef.current) {
        reconcileTimerRef.current = setTimeout(() => {
          reconcileTimerRef.current = undefined;
          void loadRef.current?.({ reset: true });
        }, 750);
      }
      return;
    }

    const currentTime = new Date(currentLead.last_activity_at || currentLead.created_at).getTime();
    if (!Number.isFinite(incomingTime) || incomingTime <= currentTime) return;

    setLeads((current) => current.map((lead) => (
      lead.id === event.lead_id
        ? { ...lead, last_activity_at: event.activity_at }
        : lead
    )));

    if (event.direction === 'inbound') {
      setNewWhatsAppLeadIds((current) => {
        const next = new Set(current);
        next.add(event.lead_id);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    if (active && isPageVisible && whatsappActivityEvent) handleWhatsAppActivity(whatsappActivityEvent);
  }, [active, handleWhatsAppActivity, isPageVisible, whatsappActivityEvent]);

  useEffect(() => () => {
    if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
  }, []);

  useEffect(() => {
    if (!active || !isPageVisible) return;
    scheduleResetLoad();
  }, [active, isPageVisible, load, reloadKey, scheduleResetLoad, queueSignalKey, view]);

  usePolling(() => scheduleResetLoad(), 5 * 60_000, [view, reloadKey, queueSignalKey, scheduleResetLoad], {
    enabled: active && view === 'queue' && queueSseConnectionState === 'disconnected' && isPageVisible,
    immediate: true,
  });

  useEffect(() => {
    if (!active || view !== 'unassigned' || !hasMore || loading || loadingMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void load();
        }
      },
      { root: null, rootMargin: '200px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [active, hasMore, load, loading, loadingMore, view]);

  const toggleSource = (source: string) => {
    setSelectedSources((current) =>
      current.includes(source) ? current.filter((item) => item !== source) : [...current, source]
    );
  };

  const toggleStatus = (status: LeadStatus | 'all') => {
    setSelectedStatuses([status]);
  };

  const handleDateFilterChange = (value: DateFilter) => {
    setSelectedDateFilter(value);
    if (value !== 'custom') {
      setCustomFrom('');
      setCustomTo('');
    }
  };

  const handleTierFilterChange = (value: TierFilter) => {
    setSelectedTierFilter(value);
  };

  const resetQueueFilters = () => {
    setSelectedSources([]);
    setSelectedStatuses(['all']);
    setSelectedTierFilter('all');
    handleDateFilterChange('all');
  };

  async function handleClaim(id: string) {
    try {
      await claimLead(id);
      onChanged();
      await load({ reset: true });
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Claim failed');
    }
  }

  const handleSelect = useCallback((id: string) => {
    setNewWhatsAppLeadIds((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
    onSelect(id);
  }, [onSelect]);

  if (desktop && view === 'queue') {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-50">
        <div className="relative flex h-14 flex-none items-center gap-2 border-b border-slate-200 bg-white px-3">
          <div className="mr-auto flex min-w-0 items-center gap-2">
            <h2 className="truncate text-sm font-semibold text-slate-900">{title}</h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-slate-600">
              {totalCount}
            </span>
          </div>
          <AgentStatusWidget agentId={me} active={active} variant="menu" />
          <button
            type="button"
            onClick={() => setShowFilters((current) => !current)}
            aria-expanded={showFilters}
            className={`inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold ${
              showFilters
                ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-none stroke-current" strokeWidth="1.8">
              <path d="M3 5h14M6 10h8M8.5 15h3" strokeLinecap="round" />
            </svg>
            <span>Filter</span>
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] text-emerald-700">{activeFilterCount}</span>
            )}
          </button>
          <select
            aria-label="Sort queue"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 focus:border-slate-400 focus:outline-none"
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>

          {showFilters && (
            <div className="absolute right-3 top-12 z-40 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-900">Filters</h3>
                <button type="button" onClick={() => setShowFilters(false)} className="text-xs text-slate-400 hover:text-slate-700" aria-label="Close filters">✕</button>
              </div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Date
                <select
                  aria-label="Filter queue by creation date"
                  value={selectedDateFilter}
                  onChange={(e) => handleDateFilterChange(e.target.value as DateFilter)}
                  className="mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium normal-case tracking-normal text-slate-700"
                >
                  {DATE_FILTERS.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
              </label>
              {selectedDateFilter === 'custom' && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <label className="text-[10px] font-medium text-slate-500">
                    From
                    <input
                      type="date"
                      aria-label="Queue creation date from"
                      value={customFrom}
                      max={customTo || undefined}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-2 text-[11px] text-slate-700"
                    />
                  </label>
                  <label className="text-[10px] font-medium text-slate-500">
                    To
                    <input
                      type="date"
                      aria-label="Queue creation date to"
                      value={customTo}
                      min={customFrom || undefined}
                      onChange={(e) => setCustomTo(e.target.value)}
                      className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-2 text-[11px] text-slate-700"
                    />
                  </label>
                </div>
              )}
              <fieldset className="mt-3">
                <legend className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Source</legend>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {SOURCE_FILTERS.map((source) => {
                    const checked = selectedSources.includes(source.value);
                    return (
                      <button
                        key={source.value}
                        type="button"
                        onClick={() => toggleSource(source.value)}
                        aria-pressed={checked}
                        className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${
                          checked
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {source.label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
              <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
                <button type="button" onClick={resetQueueFilters} className="h-8 px-2 text-xs font-semibold text-slate-500 hover:text-slate-800">Reset</button>
                <button type="button" onClick={() => setShowFilters(false)} className="h-8 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700">Apply</button>
              </div>
            </div>
          )}
        </div>

        <div className="flex h-12 flex-none items-center gap-1.5 overflow-x-auto border-b border-slate-200 bg-white px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <select
            aria-label="Filter queue by status"
            value={selectedStatuses.includes('all') ? 'all' : selectedStatuses[0] || 'all'}
            onChange={(e) => toggleStatus(e.target.value as LeadStatus | 'all')}
            className="h-8 flex-none rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-semibold text-slate-700 focus:outline-none"
          >
            {STATUS_FILTERS.map((status) => (
              <option key={status.value} value={status.value}>{status.value === 'all' ? 'All leads' : status.label}</option>
            ))}
          </select>
          <select
            aria-label="Filter queue by date"
            value={selectedDateFilter}
            onChange={(e) => {
              const value = e.target.value as DateFilter;
              handleDateFilterChange(value);
              if (value === 'custom') setShowFilters(true);
            }}
            className="h-8 flex-none rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-semibold text-slate-700 focus:outline-none"
          >
            {DATE_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>{filter.label}</option>
            ))}
          </select>
          <span className="mx-0.5 h-5 w-px flex-none bg-slate-200" />
          {TIER_FILTERS.map((filter) => {
            const checked = selectedTierFilter === filter.value;
            return (
              <button
                key={filter.value}
                type="button"
                onClick={() => handleTierFilterChange(filter.value)}
                aria-pressed={checked}
                className={`inline-flex h-7 flex-none items-center gap-1 rounded-full border px-2 text-[10px] font-semibold ${
                  checked ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : filter.tone
                }`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${filter.value === '1' ? 'bg-red-400' : filter.value === '2' ? 'bg-amber-400' : 'bg-slate-300'}`} />
                {filter.label}
              </button>
            );
          })}
          {activeFilterCount > 0 && (
            <button type="button" onClick={resetQueueFilters} className="h-7 flex-none px-1 text-[10px] font-semibold text-slate-500 hover:text-slate-800">Clear</button>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-2 pb-16">
          {rawRings ? (
            <p className="pb-2 text-[11px] text-slate-500">
              {rawRings} archived automatically
              {canRestoreArchived && (
                <button type="button" onClick={() => setShowArchived((v) => !v)} className="ml-2 font-semibold text-emerald-700 underline">
                  {showArchived ? 'Back to list' : 'View archived'}
                </button>
              )}
            </p>
          ) : null}
          {showArchived && <ArchivedLeads onSelect={handleSelect} onRestored={onChanged} />}
          {view === 'queue' && !showArchived && <WaitingForYou active={active} reloadKey={reloadKey} onSelect={handleSelect} />}
          {refreshing && !loading && <p className="pb-2 text-center text-[10px] uppercase tracking-wider text-slate-400">Updating…</p>}
          {loading && <p className="py-10 text-center text-sm text-slate-500">Loading…</p>}
          {err && <p className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-700">{err}</p>}
          {!loading && !err && visibleLeads.length === 0 && (
            <div className="flex min-h-48 items-center justify-center text-center">
              <div>
                <p className="text-sm font-medium text-slate-600">No leads in your queue</p>
                <p className="mt-1 text-xs text-slate-400">Try changing your filters.</p>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {visibleLeads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onSelect={handleSelect}
                me={me}
                showStatus
                selected={selectedId === lead.id}
                whatsappActivity={newWhatsAppLeadIds.has(lead.id)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 px-4 py-3">
      {view === 'queue' && <AgentStatusWidget agentId={me} active={active} />}

      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-[11px] text-slate-500">{view === 'queue' ? 'Active leads that need attention' : subtitle}</div>
        </div>
        {view === 'queue' && (
          <button
            type="button"
            onClick={() => setShowFilters((current) => !current)}
            aria-expanded={showFilters}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 shadow-sm"
          >
            <svg aria-hidden="true" viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-none stroke-current" strokeWidth="1.8">
              <path d="M3 5h14M6 10h8M8.5 15h3" strokeLinecap="round" />
            </svg>
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] text-emerald-700">{activeFilterCount}</span>
            )}
            <span className={`text-[10px] transition-transform ${showFilters ? 'rotate-180' : ''}`}>⌄</span>
          </button>
        )}
        {refreshing && <span className="text-[10px] uppercase tracking-wider text-slate-400">…updating</span>}
      </div>

      {view === 'unassigned' && (
        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
          <div className="mb-3 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handleTierFilterChange(selectedTierFilter === '1' ? 'all' : '1')}
              className={`rounded-2xl border px-3 py-2 transition-colors ${
                selectedTierFilter === '1'
                  ? 'border-red-400 bg-red-100 ring-2 ring-red-400 ring-offset-1'
                  : 'border-red-100 bg-red-50 hover:bg-red-100'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider text-red-500">Hot</div>
              <div className="mt-1 text-lg font-semibold text-red-700">{tierCounts.hot}</div>
            </button>
            <button
              type="button"
              onClick={() => handleTierFilterChange(selectedTierFilter === '2' ? 'all' : '2')}
              className={`rounded-2xl border px-3 py-2 transition-colors ${
                selectedTierFilter === '2'
                  ? 'border-amber-400 bg-amber-100 ring-2 ring-amber-400 ring-offset-1'
                  : 'border-amber-100 bg-amber-50 hover:bg-amber-100'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider text-amber-500">Warm</div>
              <div className="mt-1 text-lg font-semibold text-amber-700">{tierCounts.warm}</div>
            </button>
            <button
              type="button"
              onClick={() => handleTierFilterChange(selectedTierFilter === '3' ? 'all' : '3')}
              className={`rounded-2xl border px-3 py-2 transition-colors ${
                selectedTierFilter === '3'
                  ? 'border-slate-400 bg-slate-100 ring-2 ring-slate-400 ring-offset-1'
                  : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Cold</div>
              <div className="mt-1 text-lg font-semibold text-slate-700">{tierCounts.cold}</div>
            </button>
          </div>

          {/* Date + Source filter bar — compact single row */}
          <div className="mb-2 flex items-center gap-2">
            <select
              aria-label="Filter unassigned leads by creation date"
              value={selectedDateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value as DateFilter)}
              className={`h-8 shrink-0 rounded-lg border px-2.5 text-[11px] font-semibold focus:outline-none ${
                selectedDateFilter !== 'all'
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              {DATE_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>{filter.label}</option>
              ))}
            </select>
            {selectedDateFilter === 'custom' && (
              <input
                type="date"
                aria-label="Filter unassigned leads by specific date"
                value={customFrom}
                onChange={(e) => {
                  const date = e.target.value;
                  setCustomFrom(date);
                  setCustomTo(date);
                }}
                className="h-8 w-[8rem] shrink-0 rounded-lg border border-slate-200 px-2 text-[11px] text-slate-700"
              />
            )}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Source</span>
              {(selectedSources.length > 0 || selectedTierFilter !== 'all' || selectedDateFilter !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSources([]);
                    setSelectedTierFilter('all');
                    handleDateFilterChange('all');
                  }}
                  className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {SOURCE_FILTERS.map((source) => {
              const checked = selectedSources.includes(source.value);
              const count = sourceCounts[source.value] || 0;
              return (
                <button
                  key={source.value}
                  type="button"
                  onClick={() => toggleSource(source.value)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                    checked
                      ? 'border-wechat-accent bg-wechat-accent text-white shadow-sm'
                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                  aria-pressed={checked}
                  >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      checked ? 'bg-white' : 'bg-slate-300'
                    }`}
                  />
                  <span className="font-semibold tabular-nums">{count}</span>
                  <span>{source.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {view === 'queue' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <select
              aria-label="Filter queue by status"
              value={selectedStatuses.includes('all') ? 'all' : selectedStatuses[0] || 'all'}
              onChange={(e) => toggleStatus(e.target.value as LeadStatus | 'all')}
              className="h-10 flex-none rounded-xl border border-slate-200 bg-white px-2.5 text-xs font-semibold text-slate-700 focus:border-slate-400 focus:outline-none"
            >
              {STATUS_FILTERS.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowFilters(true)}
              className="inline-flex h-10 flex-none items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700"
            >
              <span>{selectedDateLabel}</span>
              <span aria-hidden="true">▣</span>
            </button>
            {TIER_FILTERS.map((filter) => {
              const checked = selectedTierFilter === filter.value;
              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => handleTierFilterChange(filter.value)}
                  className={`inline-flex h-10 flex-none items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-xs font-semibold transition-colors ${
                    checked
                      ? 'border-wechat-accent bg-wechat-accent text-white shadow-sm'
                      : filter.tone
                  }`}
                  aria-pressed={checked}
                >
                  <span
                    className={`h-2 w-2 rounded-full ${
                      checked
                        ? 'bg-white'
                        : filter.value === '1'
                          ? 'bg-red-400'
                          : filter.value === '2'
                              ? 'bg-amber-400'
                              : 'bg-slate-300'
                      }`}
                    />
                    <span>{filter.label}</span>
                  </button>
                );
              })}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetQueueFilters}
                className="h-10 flex-none px-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
              >
                Reset
              </button>
            )}
          </div>

          {showFilters && (
            <div className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
              <div className="flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <select
                  aria-label="Filter queue by creation date"
                  value={selectedDateFilter}
                  onChange={(e) => handleDateFilterChange(e.target.value as DateFilter)}
                  className="h-9 flex-none rounded-lg border border-slate-200 bg-slate-50 px-2.5 text-xs font-semibold text-slate-700"
                >
                  {DATE_FILTERS.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
                {selectedDateFilter === 'custom' && (
                  <>
                    <input
                      type="date"
                      aria-label="Queue creation date from"
                      value={customFrom}
                      max={customTo || undefined}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="h-9 w-[8.5rem] flex-none rounded-lg border border-slate-200 bg-slate-50 px-2 text-xs text-slate-700"
                    />
                    <span className="text-xs text-slate-400">–</span>
                    <input
                      type="date"
                      aria-label="Queue creation date to"
                      value={customTo}
                      min={customFrom || undefined}
                      onChange={(e) => setCustomTo(e.target.value)}
                      className="h-9 w-[8.5rem] flex-none rounded-lg border border-slate-200 bg-slate-50 px-2 text-xs text-slate-700"
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex min-h-6 items-center justify-between gap-2 text-[11px] text-slate-500">
        <span>
          {view === 'queue' ? `Total in my queue: ${totalCount}` : `Total unassigned leads: ${totalCount}`}
          {rawRings ? <span className="ml-1 text-slate-400">· {rawRings} archived automatically</span> : null}
          {canRestoreArchived && rawRings ? (
            <button type="button" onClick={() => setShowArchived((v) => !v)} className="ml-2 font-semibold text-emerald-700 underline">
              {showArchived ? 'Back to list' : 'View archived'}
            </button>
          ) : null}
        </span>
        {view === 'queue' && (
          <label className="inline-flex items-center gap-1">
            <span>Sort:</span>
            <select
              aria-label="Sort queue"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
              className="bg-transparent font-semibold text-slate-700 focus:outline-none"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </label>
        )}
      </div>

      {showArchived && <ArchivedLeads onSelect={handleSelect} onRestored={onChanged} />}
      {view === 'queue' && !showArchived && <WaitingForYou active={active} reloadKey={reloadKey} onSelect={handleSelect} />}
      {loading && <p className="py-10 text-center text-sm text-slate-500">Loading…</p>}
      {err && <p className="rounded-lg bg-red-50 p-3 text-center text-sm text-red-700">{err}</p>}
      {!loading && !err && leads.length === 0 && (
        <p className="py-16 text-center text-sm text-slate-500">
          {view === 'unassigned'
            ? 'No unassigned leads right now.'
            : 'Nothing in your queue right now.'}
        </p>
      )}

      <div className="space-y-3">
        {visibleLeads.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            onSelect={handleSelect}
            me={me}
            showStatus
            selected={selectedId === l.id}
            whatsappActivity={newWhatsAppLeadIds.has(l.id)}
            action={
              view === 'unassigned'
                ? {
                    label: 'Assign to Me',
                    onClick: handleClaim,
                  }
                : undefined
            }
          />
        ))}
      </div>

      {view === 'unassigned' && hasMore && (
        <div ref={sentinelRef} className="min-h-12 py-3 text-center text-xs text-slate-400">
          {loadingMore ? 'Loading more leads…' : 'Scroll to load more'}
        </div>
      )}
    </div>
  );
}
